import { IWBeneficiary } from '@/entities/interwills';
import { CollectionName, getCollectionDtoIdPrefix } from '@/shared/lib/sj-orm';
import { DtoId } from '@/shared/lib/sj-orm/models/base.dto';
import { BeneficiaryDto } from '@/shared/lib/sj-orm/models/beneficiary.dto';
import { UnregBeneficiaryData } from '@/widgets/modals/containers/interwill/CreateBeneficiaryModalV2/types';

export const MAX_WITNESSES = 3;
export * from './questions';

export const getBeneficiaryFullName = (
  beneficiary: Pick<BeneficiaryDto, 'firstName' | 'lastName'>,
) => {
  return `${beneficiary.firstName} ${beneficiary.lastName}`;
};

export const isFilled = <T>(value: T, options?: { ignoreKeys?: string[] }): boolean => {
  if (value === undefined || value === null) return false;
  const ignoreKeys = options?.ignoreKeys ?? [];

  if (typeof value === 'string') {
    return value.length > 0;
  }

  if (typeof value !== 'object') {
    return true;
  }

  if (Array.isArray(value)) {
    return value.every((el) => isFilled(el, options));
  }

  return Object.entries(value).every(([key, val]) =>
    ignoreKeys.includes(key) ? true : isFilled(val, options),
  );
};

export const isBeneficiaryFilled = (
  beneficiary: IWBeneficiary,
  options?: {
    ignoreUndefinedCitizenship?: boolean;
    ignoreUndefinedResidence?: boolean;
  },
) => {
  const ignoreKeys = [
    'tax',
    'secondName',
    'email',
    'phone',
    'phoneNumber',
    'phoneCode',
    'occupation',
    'hasAccount',
    'userDetailsId',
    'apt',
    'area',
  ];

  if (options?.ignoreUndefinedCitizenship && beneficiary.primaryCitizenship === undefined) {
    ignoreKeys.push('primaryCitizenship');
  }

  if (options?.ignoreUndefinedResidence && beneficiary.primaryResidence === undefined) {
    ignoreKeys.push('primaryResidence');
  }

  return isFilled(
    {
      ...getEmptyIwBeneficiary(),
      ...beneficiary,
    },
    {
      ignoreKeys,
    },
  );
};

export const getEmptyIwBeneficiary = (options?: { noCitizenship?: boolean }): IWBeneficiary => {
  const obj = {
    firstName: undefined,
    lastName: undefined,
    secondName: undefined,
    birthDate: undefined,
    gender: undefined,
    primaryCitizenship: undefined,
    primaryResidence: undefined,
    otherCitizenships: [],
    otherResidences: [],
  };
  if (options?.noCitizenship) {
    delete obj.primaryCitizenship;
  }
  return obj;
};

export const unregBeneficiaryToIWBen = (data: UnregBeneficiaryData): IWBeneficiary => {
  const obj: IWBeneficiary = {
    id: data.id,
    firstName: data.firstName,
    lastName: data.lastName,
    secondName: data.secondName,
    gender: data.gender,
    birthDate: data.dateOfBirth,
    otherCitizenships: [],
    otherResidences: [],

    isUnreg: true,
  };

  if (data.countryCitizenship) {
    obj.primaryCitizenship = {
      country: data.countryCitizenship,
      documentType: data.documentType,
      issueDate: data.issueDate,
      number: data.documentNumber,
    };
  }

  if (data.countryResidence) {
    obj.primaryResidence = {
      country: data.countryResidence,
      apt: data.apartment,
      area: data.district,
      city: data.city,
      streetBuilding: data.street,
    };
  }

  if (data.otherCitizensips) {
    obj.otherCitizenships = data.otherCitizensips.map((item) => ({
      country: item.countryCitizenship,
      documentType: item.documentType,
      issueDate: item.issueDate,
      number: item.documentNumber,
    }));
  }

  if (data.otherResidence) {
    obj.otherResidences = data.otherResidence.map((item) => ({
      country: item.countryResidence,
      apt: item.apartment,
      area: item.district,
      city: item.city,
      streetBuilding: item.street,
    }));
  }

  if (data.phoneCode) {
    obj.phoneCode = data.phoneCode;
  }

  if (data.phoneNumber) {
    obj.phoneNumber = data.phoneNumber;
  }

  if (data.email) {
    obj.email = data.email;
  }

  return obj;
};

export const isInterwillDtoId = (id: DtoId) => {
  return id.startsWith(getCollectionDtoIdPrefix(CollectionName.INTERWILLS));
};
